import React, { useCallback, useEffect, useState } from "react";
import { Users, RefreshCw, Mail, MousePointerClick, Globe, BookOpen, ExternalLink, Download, Search } from "lucide-react";
import { adminAudienceAnalytics, adminListSubscribers, adminSubscribersCsvUrl } from "../../lib/api";

const StatTile = ({ label, value, testid, sub = null }) => (
  <div
    data-testid={testid}
    className="border border-[#2D2A26] bg-[#0F0F0F] px-4 py-3"
  >
    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
      {label}
    </div>
    <div className="font-display text-2xl text-[#E4DFD3] mt-1">{value ?? "—"}</div>
    {sub && (
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065] mt-1">
        {sub}
      </div>
    )}
  </div>
);

const Row = ({ label, value, max, sub = null, href = null }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value * 100) / max)) : 0;
  const Content = (
    <div className="flex-1 min-w-0">
      <div className="text-xs text-[#E4DFD3] truncate">{label}</div>
      {sub && (
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065] truncate">
          {sub}
        </div>
      )}
      <div className="h-1 bg-[#1a1a1a] mt-1 relative">
        <div
          className="absolute inset-y-0 left-0 bg-[#D92D20]/70"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
  return (
    <div className="flex items-center gap-3 mb-1.5">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-0 hover:text-[#D92D20] inline-flex items-center gap-1.5"
          title={href}
        >
          {Content}
          <ExternalLink size={10} className="text-[#757065] shrink-0" />
        </a>
      ) : (
        Content
      )}
      <div className="font-mono text-xs text-[#CBB892] tabular-nums w-12 text-right">
        {value}
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, count, children }) => (
  <div className="border border-[#2D2A26] bg-[#131313] p-5">
    <div className="flex items-baseline justify-between mb-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-2">
        {Icon && <Icon size={12} />} // {title}
      </div>
      {count !== undefined && (
        <div className="font-mono text-[10px] text-[#757065]">{count}</div>
      )}
    </div>
    {children}
  </div>
);

const Sparkline = ({ daily, color = "#D92D20" }) => {
  if (!daily?.length) {
    return (
      <div className="text-[#757065] text-xs italic">No activity in this window yet.</div>
    );
  }
  const max = Math.max(1, ...daily.map((d) => d.count));
  return (
    <>
      <div className="flex items-end gap-1 h-16 mt-2" data-testid="audience-sparkline">
        {daily.map((d) => {
          const h = Math.max(2, Math.round((d.count * 100) / max));
          return (
            <div
              key={d.date}
              title={`${d.date}: ${d.count}`}
              className="flex-1 transition-colors"
              style={{ height: `${h}%`, backgroundColor: color, opacity: 0.65 }}
            />
          );
        })}
      </div>
      <div className="flex justify-between font-mono text-[10px] text-[#757065] mt-2">
        <span>{daily[0]?.date || ""}</span>
        <span>{daily[daily.length - 1]?.date || ""}</span>
      </div>
    </>
  );
};

export const AudienceAnalyticsPanel = ({ token, userToken = null }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d = days) => {
    setLoading(true);
    try {
      const res = await adminAudienceAnalytics(token, d, userToken);
      setData(res);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, userToken, days]);

  useEffect(() => { if (token || userToken) load(days); }, [token, userToken, days, load]);

  const subs = data?.subscribers || {};
  const ob = data?.outbound || {};
  const maxSubDossier = subs.top_dossiers?.[0]?.count || 0;
  const maxObUrl = ob.top_urls?.[0]?.count || 0;
  const maxObHost = ob.top_hosts?.[0]?.count || 0;
  const maxObDossier = ob.top_dossiers?.[0]?.count || 0;
  const maxSrc = subs.by_source?.[0]?.count || 0;
  const maxType = ob.by_link_type?.[0]?.count || 0;

  return (
    <section data-testid="audience-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Users size={14} /> // AUDIENCE & ENGAGEMENT
          </div>
          <h2 className="font-display text-3xl text-[#E4DFD3]">Subscribers & Outbound Clicks</h2>
          <p className="text-[#A39E93] text-sm mt-1 max-w-2xl">
            First-party engagement signal: who's joining the briefing list, where they signed
            up, and which dossiers and outbound links are pulling the real attention.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="audience-days-select"
            className="bg-[#0F0F0F] border border-[#2D2A26] text-[#E4DFD3] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2"
          >
            <option value={7}>last 7 days</option>
            <option value={30}>last 30 days</option>
            <option value={90}>last 90 days</option>
          </select>
          <button
            onClick={() => load(days)}
            data-testid="audience-refresh-btn"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Top-line tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatTile testid="audience-total-active" label="Active Subscribers" value={subs.total_active} />
        <StatTile testid="audience-new" label={`New (${days}d)`} value={subs.new_in_window} />
        <StatTile testid="audience-unsubscribed" label="Unsubscribed" value={subs.total_unsubscribed} />
        <StatTile testid="audience-clicks" label={`Outbound Clicks (${days}d)`} value={ob.total} />
        <StatTile
          testid="audience-click-rate"
          label="Avg Clicks / Day"
          value={ob.total && days ? (ob.total / days).toFixed(1) : "—"}
        />
      </div>

      {/* Trend rows */}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section title="SUBSCRIBER GROWTH" icon={Mail} count={`${subs.daily?.length || 0}d`}>
          <Sparkline daily={subs.daily || []} color="#5DD4B3" />
        </Section>
        <Section title="OUTBOUND CLICKS / DAY" icon={MousePointerClick} count={`${ob.daily?.length || 0}d`}>
          <Sparkline daily={ob.daily || []} color="#D92D20" />
        </Section>
      </div>

      {/* Breakdowns: sources & link types */}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section title="SIGNUP SOURCE" count={subs.by_source?.length || 0}>
          {(subs.by_source || []).map((s) => (
            <Row key={s.source} label={s.source} value={s.count} max={maxSrc} />
          ))}
          {!subs.by_source?.length && (
            <div className="text-[#757065] text-xs italic">No signups yet.</div>
          )}
        </Section>
        <Section title="LINK TYPE" count={ob.by_link_type?.length || 0}>
          {(ob.by_link_type || []).map((t) => (
            <Row key={t.link_type} label={t.link_type} value={t.count} max={maxType} />
          ))}
          {!ob.by_link_type?.length && (
            <div className="text-[#757065] text-xs italic">No outbound clicks yet.</div>
          )}
        </Section>
      </div>

      {/* Top dossiers — subscribers vs clicks */}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section title="TOP DOSSIERS BY SUBSCRIBERS" count={subs.top_dossiers?.length || 0}>
          {(subs.top_dossiers || []).map((d) => (
            <Row
              key={`s-${d.slug}`}
              label={d.title}
              sub={d.slug}
              value={d.count}
              max={maxSubDossier}
              href={`/topic/${d.slug}`}
            />
          ))}
          {!subs.top_dossiers?.length && (
            <div className="text-[#757065] text-xs italic">No dossier-bound signups yet.</div>
          )}
        </Section>
        <Section title="TOP DOSSIERS BY OUTBOUND CLICKS" count={ob.top_dossiers?.length || 0}>
          {(ob.top_dossiers || []).map((d) => (
            <Row
              key={`c-${d.slug}`}
              label={d.title}
              sub={d.slug}
              value={d.count}
              max={maxObDossier}
              href={`/topic/${d.slug}`}
            />
          ))}
          {!ob.top_dossiers?.length && (
            <div className="text-[#757065] text-xs italic">No outbound clicks attributed to a dossier yet.</div>
          )}
        </Section>
      </div>

      {/* Top URLs + Hosts */}
      <div className="grid md:grid-cols-2 gap-3">
        <Section title="TOP OUTBOUND URLS" icon={BookOpen} count={ob.top_urls?.length || 0}>
          {(ob.top_urls || []).map((u, i) => (
            <Row
              key={`u-${i}-${u.url}`}
              label={u.publisher || u.host || u.url}
              sub={u.url}
              value={u.count}
              max={maxObUrl}
              href={u.url}
            />
          ))}
          {!ob.top_urls?.length && (
            <div className="text-[#757065] text-xs italic">No outbound clicks yet.</div>
          )}
        </Section>
        <Section title="TOP DESTINATIONS (HOST)" icon={Globe} count={ob.top_hosts?.length || 0}>
          {(ob.top_hosts || []).map((h) => (
            <Row key={`h-${h.host}`} label={h.host} value={h.count} max={maxObHost} />
          ))}
          {!ob.top_hosts?.length && (
            <div className="text-[#757065] text-xs italic">No outbound clicks yet.</div>
          )}
        </Section>
      </div>

      {/* Subscriber list — searchable + CSV export */}
      <SubscribersTable token={token} userToken={userToken} />
    </section>
  );
};

const STATUS_COLOR = { active: "#5DD4B3", unsubscribed: "#757065", bounced: "#D92D20" };

const SubscribersTable = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminListSubscribers(token, { q, source, status, skip, limit }, userToken);
      setRows(r.rows || []); setTotal(r.total || 0); setSources(r.sources || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [token, userToken, q, source, status, skip]);
  useEffect(() => { load(); }, [load]);

  // Reset paging when filters change
  useEffect(() => { setSkip(0); }, [q, source, status]);

  const csv = adminSubscribersCsvUrl(token, { q, source, status });

  return (
    <div className="border border-[#2D2A26] bg-[#131313] mt-4" data-testid="subscribers-table">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#2D2A26]">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-2">
          <Users size={12} /> // SUBSCRIBER LIST
        </div>
        <span className="font-mono text-[10px] text-[#757065]">{total} matching</span>
        <div className="flex-1" />
        <div className="relative">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#757065]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search email…"
            data-testid="subscribers-search-input"
            className="bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none pl-7 pr-3 py-1.5 font-mono text-xs text-[#E4DFD3] placeholder:text-[#757065]"
          />
        </div>
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          data-testid="subscribers-source-filter"
          className="bg-[#0F0F0F] border border-[#2D2A26] text-[#E4DFD3] font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1.5"
        >
          <option value="">all sources</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="subscribers-status-filter"
          className="bg-[#0F0F0F] border border-[#2D2A26] text-[#E4DFD3] font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1.5"
        >
          <option value="">all status</option>
          <option value="active">active</option>
          <option value="unsubscribed">unsubscribed</option>
          <option value="bounced">bounced</option>
        </select>
        <a
          href={csv}
          download
          data-testid="subscribers-csv-download"
          className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <Download size={11} /> CSV
        </a>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] border-b border-[#2D2A26]">
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Source</th>
              <th className="px-4 py-2">Dossier</th>
              <th className="px-4 py-2">Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-[10px] text-[#757065]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-[10px] text-[#757065]">No subscribers match.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id || r.email} className="border-b border-[#2D2A26]" data-testid={`subscriber-row-${r.email}`}>
                <td className="px-4 py-2 text-[#E4DFD3] break-all">{r.email}</td>
                <td className="px-4 py-2">
                  <span
                    style={{ color: STATUS_COLOR[r.status] || "#A39E93", borderColor: STATUS_COLOR[r.status] || "#454038" }}
                    className="font-mono text-[9px] uppercase tracking-[0.25em] border px-2 py-0.5"
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-[10px] text-[#A39E93]">{r.source || "—"}</td>
                <td className="px-4 py-2 font-mono text-[10px] text-[#757065] break-all">
                  {r.topic_slug ? <a href={`/topic/${r.topic_slug}`} target="_blank" rel="noopener noreferrer" className="text-[#A39E93] hover:text-[#D92D20] inline-flex items-center gap-1">{r.topic_slug} <ExternalLink size={9} /></a> : "—"}
                  {r.ref_path ? <div className="text-[#757065]">{r.ref_path}</div> : null}
                </td>
                <td className="px-4 py-2 font-mono text-[10px] text-[#757065] whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(total > limit) && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-[#2D2A26]">
          <button
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
            data-testid="subscribers-prev"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#D92D20] disabled:opacity-40"
          >
            ← prev
          </button>
          <span className="font-mono text-[10px] text-[#757065]">
            {skip + 1}–{Math.min(total, skip + rows.length)} of {total}
          </span>
          <button
            onClick={() => setSkip(skip + limit)}
            disabled={skip + rows.length >= total}
            data-testid="subscribers-next"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#D92D20] disabled:opacity-40"
          >
            next →
          </button>
        </div>
      )}
    </div>
  );
};

export default AudienceAnalyticsPanel;
